import HostedCreditApplicationDialog from 'myenergy/selectionComponent/loanApplicationButton/generic/hostedForms/HostedCreditApplicationDialog'
import { useGetApplicationDisclosures } from 'myenergy/selectionComponent/loanApplicationButton/generic/hostedForms/utils'
import {
  BaseThemeType,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  Dialog,
  LoadingDots,
  styled,
  useMediaQuery,
} from 'opensolar-ui'
import { useState } from 'react'
import { CheckoutFinancingPricingOption, CheckoutFinancingPricingParams } from '../types'
import { removeCountryCodeFromPhone } from '../utils'

type PropTypes = {
  pricingOption: CheckoutFinancingPricingOption
  projectId: number
  orgId: number
  systemUuid: string
  paymentOptionId: number
  integration: string
  open: boolean
  onClose: () => void
  closeRootDialog: () => void
  pricingParams: CheckoutFinancingPricingParams
  setHideDialog: (hide: boolean) => void
  rank?: number
}

const LoadingWrapper = styled('div')<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: isMobile ? '100vh' : '500px',
  minWidth: isMobile ? '100vw' : '500px',
}))

const MarketplaceCTADialog = (props: PropTypes) => {
  const [isLoading, setIsLoading] = useState(false)
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  const onClose = () => {
    props.onClose()
    props.setHideDialog(false)
  }

  const { hostedDisclosures, logoURL, integrationDisplayName, formJSON, projectPreview } = useGetApplicationDisclosures(
    props.orgId,
    props.projectId,
    props.systemUuid,
    props.paymentOptionId,
    null,
    props.integration,
    setIsLoading
  )

  if (!formJSON || !projectPreview) {
    return (
      <Dialog open={true} onClose={onClose} fullScreen={isMobile}>
        <LoadingWrapper isMobile={isMobile}>
          <LoadingDots text="Your finance application is being prepared..." />
        </LoadingWrapper>
      </Dialog>
    )
  }

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <HostedCreditApplicationDialog
        onClose={onClose}
        closeRootDialog={props.closeRootDialog}
        orgId={props.orgId}
        projectId={props.projectId}
        systemUuid={props.systemUuid}
        paymentOptionId={props.paymentOptionId}
        integration={props.integration}
        project={null}
        initialValues={{
          applicant: {
            firstName: projectPreview?.contact?.first_name,
            lastName: projectPreview?.contact?.family_name,
            email: projectPreview?.contact?.email,
            mobilePhone: removeCountryCodeFromPhone(projectPreview?.contact?.phone),
          },
          currentAddress: {
            addressLine1:
              typeof projectPreview?.address === 'string'
                ? projectPreview.address.replace(/^\d+\s/, '')
                : projectPreview?.address,
            buildingNo: typeof projectPreview?.address === 'string' ? projectPreview.address.match(/^\d+/)?.[0] : null,
            city: projectPreview?.city,
            postcode: projectPreview?.zip,
            county: projectPreview?.county,
          },
          currentAddressIsInstallationSite: true,
          loan: {
            depositAmount: props.pricingOption?.upfront_payment || props.pricingParams?.upfront_payment,
            loanAmount: props.pricingOption?.financed_amount,
            repaymentTermInMonths: props.pricingOption?.term,
            cashPriceAmount: props.pricingOption?.system_price,
            external_id: props.pricingOption?.external_id ?? undefined,
          },
        }}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        isMarketplace={true}
        marketplaceProductDetails={{
          interest_rate: props.pricingOption?.apr,
          total_repayable: props.pricingOption?.total_repayable,
          monthly_payment: props.pricingOption?.monthly_payment,
          loan_term_months: props.pricingOption?.term,
          logo_url: props.pricingOption?.lender_logo_url,
          financed_amount: props.pricingOption?.financed_amount,
        }}
        formJSON={formJSON}
        disclosures={[]} //passing disclosures prevents us from making a redudant call to fetch json form data
        logoURL={logoURL}
      />
    </ComponentVersionsInherit>
  )
}

export default MarketplaceCTADialog
